import { useTranslation } from "react-i18next"

import type { FairUseDowngradeModelOption, FairUseReasoningEffort } from "@/api/admin/fair-use"

import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

import { FAIR_USE_REASONING_EFFORTS, normalizeFairUseReasoningEffort } from "./fair-use-utils"

/**
 * English names for the effort stops, used as i18n default values only — the
 * ladder ids are the backend's (`VALID_REASONING_EFFORTS`) and stay
 * untranslated on the wire, while the admin reads real words.
 */
const REASONING_EFFORT_FALLBACK_LABELS: Record<FairUseReasoningEffort, string> = {
  none: "None",
  minimal: "Minimal",
  low: "Low",
  medium: "Medium",
  high: "High",
  xhigh: "Very high",
  max: "Maximum",
}

type FairUseDowngradeFieldsProps = {
  model: string
  effort: FairUseReasoningEffort
  /** Deployments the platform can fall back to; `[]` on a backend that sends none. */
  modelOptions: FairUseDowngradeModelOption[]
  /**
   * True when the platform itself reported that the current value resolves to
   * a real deployment, even though it matches no option id exactly (a legacy
   * suffix-less config). Only ever true for the UNEDITED stored value — the
   * caller stops passing it the moment the admin picks something else, since
   * the platform's verdict describes what it last read, not what is in the
   * form.
   */
  isModelResolvedByPlatform: boolean
  /** The step the fallback starts at, for the field's label. */
  fallbackPercent: string
  onModelChange: (model: string) => void
  onEffortChange: (effort: FairUseReasoningEffort) => void
}

/**
 * The two halves of what a fallback turn runs as, rendered as a pair of fields
 * below the ladder in the Limits panel.
 *
 * They belong together: swapping in the cheaper model while leaving the user's
 * reasoning effort untouched leaves the larger saving on the table, because
 * reasoning tokens are billed as output tokens. Both are DELEGABLE — they tune
 * enforcement rather than the commercial envelope — so neither is gated on
 * platform-admin rights.
 *
 * Both are enumerations, so both are selects. The effort ladder is validated
 * by the backend (free text there could only ever produce a 400
 * `invalid_config`); the model is a deployment id resolved against
 * `CONFIG_CHAT_MODELS` at ENFORCEMENT time, which is worse — a typo is
 * accepted by the PUT and only shows up as the fallback silently not
 * happening, weeks later, on someone else's bill. The platform now sends the
 * resolvable deployments, so the admin picks from what exists.
 *
 * A configured model that is not in the list is still offered as its own
 * option, flagged as not deployed: dropping it would silently rewrite the
 * stored value to whatever happened to be first, and hiding it would leave the
 * admin staring at a correct-looking panel while the fallback does nothing.
 * Naming the problem in the option itself is the only reading that explains
 * the symptom.
 */
export function FairUseDowngradeFields({
  model,
  effort,
  modelOptions,
  isModelResolvedByPlatform,
  fallbackPercent,
  onModelChange,
  onEffortChange,
}: FairUseDowngradeFieldsProps) {
  const { t } = useTranslation()

  // An exact id match OR the platform's own suffix-tolerant verdict. Warning
  // on a working legacy config would be a false alarm on the one screen meant
  // to tell the admin the truth about this setting.
  const isModelDeployed =
    modelOptions.some((option) => option.id === model) || isModelResolvedByPlatform

  return (
    <>
      <div className="space-y-1">
        <Label htmlFor="fair-use-fallback-model">
          {t("adminDashboard.fairUse.settings.fallback.modelLabel", {
            defaultValue: "Fallback model after {{percent}} %",
            percent: fallbackPercent,
          })}
        </Label>
        <Select value={model} onValueChange={onModelChange}>
          <SelectTrigger id="fair-use-fallback-model">
            <SelectValue
              placeholder={t(
                "adminDashboard.fairUse.settings.fallback.modelPlaceholder",
                "Select a model"
              )}
            />
          </SelectTrigger>
          <SelectContent>
            {/* The stored value first, and only when the platform did not
                offer it — a Radix Select whose `value` matches no item renders
                an empty trigger, which reads as "nothing is configured" for
                what is actually a misconfiguration. */}
            {model && !isModelDeployed && (
              <SelectItem value={model}>
                {t("adminDashboard.fairUse.settings.fallback.modelUnknown", {
                  defaultValue: "{{model}} — unknown, not deployed",
                  model,
                })}
              </SelectItem>
            )}
            {modelOptions.map((option) => (
              <SelectItem key={option.id} value={option.id}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-muted-foreground text-xs">
          {t(
            "adminDashboard.fairUse.settings.fallback.modelHelp",
            "Only deployed models are listed, so the fallback always works."
          )}
        </p>
        {model && !isModelDeployed && (
          <p className="text-destructive text-xs" data-testid="fair-use-fallback-model-unknown">
            {t(
              "adminDashboard.fairUse.settings.fallback.modelUnknownHint",
              "This deployment is not available on this platform, so answers stay on the user's normal model instead."
            )}
          </p>
        )}
      </div>
      <div className="space-y-1">
        <Label htmlFor="fair-use-fallback-effort">
          {t(
            "adminDashboard.fairUse.settings.fallback.effortLabel",
            "Reasoning effort on the fallback model"
          )}
        </Label>
        <Select
          value={effort}
          onValueChange={(value) => onEffortChange(normalizeFairUseReasoningEffort(value))}
        >
          <SelectTrigger id="fair-use-fallback-effort">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FAIR_USE_REASONING_EFFORTS.map((stop) => (
              <SelectItem key={stop} value={stop}>
                {t(
                  `adminDashboard.fairUse.settings.fallback.effort.${stop}`,
                  REASONING_EFFORT_FALLBACK_LABELS[stop]
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-muted-foreground text-xs">
          {t(
            "adminDashboard.fairUse.settings.fallback.effortHelp",
            "Lower effort keeps the fallback cheap."
          )}
        </p>
      </div>
    </>
  )
}
